<template>
  <div class="min-h-screen" data-theme="red">
    <NavBar :signed-in="signedIn" :user-name="userName" active-page="news" />
    <div class="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <a href="/news" class="text-sm text-blue-700 hover:text-blue-900 mb-6 inline-block">&larr; {{ t('news.back_to_list') }}</a>

      <div class="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <h1 class="text-2xl font-bold text-gray-900 mb-6">{{ editing ? t('news.edit_news') : t('news.new_news') }}</h1>

        <div v-if="formErrors.length" class="mb-4 bg-red-50 border border-red-200 rounded-lg px-4 py-3">
          <p v-for="error in formErrors" :key="error" class="text-sm text-red-700">{{ error }}</p>
        </div>

        <div v-if="flash" :class="['mb-4 px-4 py-3 rounded-lg text-sm', flash.type === 'success' ? 'bg-blue-100 text-blue-800' : 'bg-red-100 text-red-800']">{{ flash.message }}</div>

        <form @submit.prevent="submitForm" class="space-y-6">
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">{{ t('news.name') }}</label>
            <input v-model="form.title" type="text" required class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none text-sm" />
          </div>

          <!-- Cover image -->
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-2">{{ t('news.cover_image') }}</label>
            <div class="flex items-start gap-4">
              <div class="flex flex-col gap-2">
                <input ref="coverInput" type="file" accept="image/*" class="hidden" @change="uploadCover" />
                <button type="button" @click="$refs.coverInput.click()" :disabled="coverUploading"
                        class="px-3 py-1.5 text-xs font-medium text-blue-700 bg-blue-50 rounded-lg hover:bg-blue-100 transition">
                  {{ coverUploading ? '...' : (form.cover_image_url ? t('news.change_cover') : t('news.upload_cover')) }}
                </button>
                <button v-if="form.cover_image_url" type="button" @click="form.cover_image_url = ''; form.cover_position = ''"
                        class="px-3 py-1.5 text-xs font-medium text-red-700 bg-red-50 rounded-lg hover:bg-red-100 transition">
                  {{ t('news.remove_cover') }}
                </button>
                <button v-if="bodyHasImage && !form.cover_image_url" type="button" @click="useBodyImage"
                        class="text-xs text-blue-700 hover:underline text-left">
                  {{ t('news.use_body_image') }}
                </button>
              </div>
            </div>

            <!-- Cover crop selector -->
            <div v-if="form.cover_image_url" class="mt-3">
              <p class="text-xs text-gray-500 mb-2">{{ t('news.cover_crop_hint') }}</p>
              <div class="relative rounded-lg border border-gray-200 overflow-hidden select-none" style="max-width: 500px;" ref="cropContainer">
                <img :src="form.cover_image_url" class="w-full" ref="coverPreview" />
                <!-- Dark overlays -->
                <div class="absolute top-0 left-0 right-0 bg-black/50 pointer-events-none" :style="`height: ${cropTop}%`"></div>
                <div class="absolute bottom-0 left-0 right-0 bg-black/50 pointer-events-none" :style="`height: ${100 - cropBottom}%`"></div>
                <div class="absolute left-0 bg-black/50 pointer-events-none" :style="`top: ${cropTop}%; height: ${cropBottom - cropTop}%; width: ${cropLeft}%`"></div>
                <div class="absolute right-0 bg-black/50 pointer-events-none" :style="`top: ${cropTop}%; height: ${cropBottom - cropTop}%; width: ${100 - cropRight}%`"></div>
                <!-- Top handle -->
                <div class="absolute left-0 right-0 h-1 bg-blue-500 cursor-ns-resize z-10" :style="`top: ${cropTop}%`"
                     @mousedown.prevent="startDrag('top', $event)" @touchstart.prevent="startDrag('top', $event)">
                  <div class="absolute left-1/2 -translate-x-1/2 -top-2 w-6 h-4 bg-blue-500 rounded-t text-white text-[8px] text-center leading-4 shadow">&#9650;</div>
                </div>
                <!-- Bottom handle -->
                <div class="absolute left-0 right-0 h-1 bg-blue-500 cursor-ns-resize z-10" :style="`top: ${cropBottom}%`"
                     @mousedown.prevent="startDrag('bottom', $event)" @touchstart.prevent="startDrag('bottom', $event)">
                  <div class="absolute left-1/2 -translate-x-1/2 -bottom-2 w-6 h-4 bg-blue-500 rounded-b text-white text-[8px] text-center leading-4 shadow">&#9660;</div>
                </div>
                <!-- Left handle -->
                <div class="absolute top-0 bottom-0 w-1 bg-blue-500 cursor-ew-resize z-10" :style="`left: ${cropLeft}%`"
                     @mousedown.prevent="startDrag('left', $event)" @touchstart.prevent="startDrag('left', $event)">
                  <div class="absolute top-1/2 -translate-y-1/2 -left-2 w-4 h-6 bg-blue-500 rounded-l text-white text-[8px] text-center leading-6 shadow">&#9664;</div>
                </div>
                <!-- Right handle -->
                <div class="absolute top-0 bottom-0 w-1 bg-blue-500 cursor-ew-resize z-10" :style="`left: ${cropRight}%`"
                     @mousedown.prevent="startDrag('right', $event)" @touchstart.prevent="startDrag('right', $event)">
                  <div class="absolute top-1/2 -translate-y-1/2 -right-2 w-4 h-6 bg-blue-500 rounded-r text-white text-[8px] text-center leading-6 shadow">&#9654;</div>
                </div>
              </div>
              <!-- Preview -->
              <p class="text-xs text-gray-400 mt-3 mb-1">{{ t('news.cover_crop_preview') }}:</p>
              <div class="rounded-lg border border-gray-200 overflow-hidden" style="max-width: 500px;">
                <img :src="croppedUrl" class="w-full" />
              </div>
            </div>
          </div>

          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">{{ t('news.body') }}</label>
            <RichEditor v-model="form.body" :placeholder="t('news.body_placeholder')" :csrf-token="csrfToken" />
          </div>

          <div class="flex items-center gap-3 pt-4">
            <button type="submit" :disabled="submitting"
                    class="px-6 py-2.5 text-sm font-medium text-white bg-blue-700 rounded-lg hover:bg-blue-800 transition disabled:opacity-50">
              {{ submitting ? '...' : (editing ? t('news.save') : t('news.create')) }}
            </button>
            <a href="/news" class="px-4 py-2.5 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition">{{ t('news.cancel') }}</a>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import NavBar from './NavBar.vue'
import RichEditor from './RichEditor.vue'

const props = defineProps({ signedIn: Boolean, userName: String, newsItem: Object, editing: Boolean, csrfToken: String })
const { t } = useI18n()

const form = reactive({
  title: props.newsItem.title || '',
  body: props.newsItem.body || '',
  cover_image_url: props.newsItem.cover_image_url || '',
  cover_position: props.newsItem.cover_position || ''
})

const formErrors = ref([])
const submitting = ref(false)
const coverUploading = ref(false)
const flash = ref(null)
const coverInput = ref(null)
const coverPreview = ref(null)
const cropContainer = ref(null)

const bodyHasImage = computed(() => form.body && form.body.includes('<img'))

function useBodyImage() {
  const match = form.body.match(/<img[^>]+src="([^"]+)"/)
  if (match) form.cover_image_url = match[1]
}

// Parse stored crop: "top,bottom,left,right" or default full image
function parseCrop() {
  const c = form.cover_position || '0,100,0,100'
  const p = c.split(',').map(Number)
  return { top: p[0] || 0, bottom: p[1] || 100, left: p[2] || 0, right: p[3] || 100 }
}

const cropTop = ref(parseCrop().top)
const cropBottom = ref(parseCrop().bottom)
const cropLeft = ref(parseCrop().left)
const cropRight = ref(parseCrop().right)

function updateCropPosition() {
  form.cover_position = `${Math.round(cropTop.value)},${Math.round(cropBottom.value)},${Math.round(cropLeft.value)},${Math.round(cropRight.value)}`
}

// Generate Cloudinary URL with crop transformation
const croppedUrl = computed(() => {
  const url = form.cover_image_url
  if (!url || !url.includes('/upload/')) return url
  const t = cropTop.value / 100
  const b = cropBottom.value / 100
  const l = cropLeft.value / 100
  const r = cropRight.value / 100
  const w = r - l
  const h = b - t
  if (w >= 0.99 && h >= 0.99) return url
  const crop = `c_crop,w_${w.toFixed(2)},h_${h.toFixed(2)},x_${l.toFixed(2)},y_${t.toFixed(2)},fl_relative`
  return url.replace('/upload/', `/upload/${crop}/`)
})

function startDrag(handle, e) {
  const onMove = (ev) => {
    const rect = cropContainer.value.getBoundingClientRect()
    const clientX = ev.touches ? ev.touches[0].clientX : ev.clientX
    const clientY = ev.touches ? ev.touches[0].clientY : ev.clientY
    const xPct = Math.max(0, Math.min(100, ((clientX - rect.left) / rect.width) * 100))
    const yPct = Math.max(0, Math.min(100, ((clientY - rect.top) / rect.height) * 100))

    if (handle === 'top') cropTop.value = Math.min(yPct, cropBottom.value - 5)
    else if (handle === 'bottom') cropBottom.value = Math.max(yPct, cropTop.value + 5)
    else if (handle === 'left') cropLeft.value = Math.min(xPct, cropRight.value - 5)
    else if (handle === 'right') cropRight.value = Math.max(xPct, cropLeft.value + 5)
    updateCropPosition()
  }
  const onEnd = () => {
    document.removeEventListener('mousemove', onMove)
    document.removeEventListener('mouseup', onEnd)
    document.removeEventListener('touchmove', onMove)
    document.removeEventListener('touchend', onEnd)
  }
  document.addEventListener('mousemove', onMove)
  document.addEventListener('mouseup', onEnd)
  document.addEventListener('touchmove', onMove)
  document.addEventListener('touchend', onEnd)
}

async function uploadCover(e) {
  const file = e.target.files[0]
  if (!file) return
  e.target.value = ''
  coverUploading.value = true
  try {
    const formData = new FormData()
    formData.append('file', file)
    formData.append('type', 'news')
    const res = await fetch('/upload', { method: 'POST', headers: { 'X-CSRF-Token': props.csrfToken }, body: formData })
    if (res.ok) { const data = await res.json(); form.cover_image_url = data.url }
  } catch {}
  coverUploading.value = false
}

async function submitForm() {
  submitting.value = true; formErrors.value = []
  const method = props.editing ? 'PATCH' : 'POST'
  const url = props.editing ? `/news/${props.newsItem.id}` : '/news'
  try {
    const res = await fetch(url, {
      method,
      headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': props.csrfToken, 'Accept': 'application/json' },
      body: JSON.stringify({ news: { title: form.title, body: form.body, cover_image_url: form.cover_image_url, cover_position: form.cover_position } })
    })
    if (res.ok) {
      const data = await res.json()
      window.location.href = `/news/${data.id}`
    } else {
      formErrors.value = (await res.json()).errors || ['Error']
    }
  } catch { formErrors.value = ['Error'] }
  finally { submitting.value = false }
}
</script>
